import { BasePrinterAdapter } from './BasePrinterAdapter'
import type {
  PrinterConnectionConfig,
  PrinterTelemetry,
  PrinterCapabilities,
  CameraStream,
  OctoPrintState,
} from '../printerConnection.types'

export class OctoPrintAdapter extends BasePrinterAdapter {
  private ws: WebSocket | null = null

  constructor(config: PrinterConnectionConfig) {
    super({ ...config, port: config.port || 80 })
  }

  async connect(): Promise<void> {
    this.setConnectionStatus('connecting')

    try {
      // Test connection with version endpoint
      const response = await fetch(this.buildUrl('/api/version'), {
        headers: this.getHeaders(),
      })

      if (!response.ok) {
        throw new Error(`Connection failed: ${response.statusText}`)
      }

      const version = await response.json()
      this.state.firmwareInfo = {
        name: 'OctoPrint',
        version: version.server,
      }

      // Get capabilities
      this.state.capabilities = await this.getCapabilities()

      // Connect WebSocket for real-time updates
      this.connectWebSocket()

      this.setConnectionStatus('connected')
      this.state.printerStatus = 'idle'
      this.startPolling()
    } catch (error) {
      this.state.lastError = error instanceof Error ? error.message : 'Connection failed'
      this.setConnectionStatus('error')
      throw error
    }
  }

  async disconnect(): Promise<void> {
    this.stopPolling()
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.setConnectionStatus('disconnected')
    this.state.printerStatus = 'offline'
  }

  private connectWebSocket(): void {
    const wsUrl = this.buildWsUrl('/sockjs/websocket')
    this.ws = new WebSocket(wsUrl)

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        this.handleWebSocketMessage(data)
      } catch {
        // Ignore parse errors
      }
    }

    this.ws.onerror = () => {
      console.error('OctoPrint WebSocket error')
    }

    this.ws.onclose = () => {
      if (this.state.connectionStatus === 'connected' && this.config.autoReconnect) {
        this.handleDisconnect()
      }
    }
  }

  private handleWebSocketMessage(data: { current?: OctoPrintState }): void {
    if (data.current) {
      const state = data.current
      
      // Update printer status
      if (state.state) {
        if (state.state.flags.printing) {
          this.state.printerStatus = 'printing'
        } else if (state.state.flags.paused) {
          this.state.printerStatus = 'paused'
        } else if (state.state.flags.error) {
          this.state.printerStatus = 'error'
        } else if (state.state.flags.ready) {
          this.state.printerStatus = 'idle'
        }
      }

      // Update telemetry from WebSocket
      if (state.temperature || state.job) {
        const telemetry: PrinterTelemetry = {
          timestamp: new Date(),
          temperatures: {
            nozzle: {
              actual: state.temperature?.tool0?.actual ?? 0,
              target: state.temperature?.tool0?.target ?? 0,
            },
            bed: {
              actual: state.temperature?.bed?.actual ?? 0,
              target: state.temperature?.bed?.target ?? 0,
            },
          },
        }

        if (state.job?.progress) {
          telemetry.printProgress = state.job.progress.completion
          telemetry.printTimeElapsed = state.job.progress.printTime
          telemetry.printTimeRemaining = state.job.progress.printTimeLeft
        }

        this.updateTelemetry(telemetry)
      }
    }
  }

  async getTelemetry(): Promise<PrinterTelemetry> {
    const [printerState, jobState] = await Promise.all([
      fetch(this.buildUrl('/api/printer'), { headers: this.getHeaders() }).then((r) => r.json()),
      fetch(this.buildUrl('/api/job'), { headers: this.getHeaders() }).then((r) => r.json()),
    ])

    const telemetry: PrinterTelemetry = {
      timestamp: new Date(),
      temperatures: {
        nozzle: {
          actual: printerState.temperature?.tool0?.actual ?? 0,
          target: printerState.temperature?.tool0?.target ?? 0,
        },
        bed: {
          actual: printerState.temperature?.bed?.actual ?? 0,
          target: printerState.temperature?.bed?.target ?? 0,
        },
      },
    }

    if (jobState.progress) {
      telemetry.printProgress = jobState.progress.completion
      telemetry.printTimeElapsed = jobState.progress.printTime
      telemetry.printTimeRemaining = jobState.progress.printTimeLeft
      this.state.currentFile = jobState.job?.file?.name
    }

    return telemetry
  }

  async getCapabilities(): Promise<PrinterCapabilities> {
    // Try to get printer profile for capabilities
    try {
      const response = await fetch(this.buildUrl('/api/printerprofiles'), {
        headers: this.getHeaders(),
      })
      const profiles = await response.json()
      const currentProfile = Object.values(profiles.profiles).find((p: any) => p.current) as any

      return {
        hasCamera: true, // OctoPrint typically has webcam support
        hasEnclosure: false,
        hasMultiMaterial: false,
        hasAutoLeveling: currentProfile?.volume?.formFactor === 'rectangular',
        hasFilamentSensor: false,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 120,
        buildVolume: {
          x: currentProfile?.volume?.width ?? 220,
          y: currentProfile?.volume?.depth ?? 220,
          z: currentProfile?.volume?.height ?? 250,
        },
      }
    } catch {
      return {
        hasCamera: true,
        hasEnclosure: false,
        hasMultiMaterial: false,
        hasAutoLeveling: false,
        hasFilamentSensor: false,
        hasPowerMonitor: false,
        maxNozzleTemp: 300,
        maxBedTemp: 120,
        buildVolume: { x: 220, y: 220, z: 250 },
      }
    }
  }

  async getCameraStream(): Promise<CameraStream | null> {
    // OctoPrint default webcam stream
    return {
      url: this.buildUrl('/webcam/?action=stream'),
      type: 'mjpeg',
    }
  }

  async startPrint(filename: string): Promise<void> {
    // First select the file
    await fetch(this.buildUrl(`/api/files/local/${filename}`), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'select', print: true }),
    })
    this.emitEvent('print_started', { filename })
  }

  async pausePrint(): Promise<void> {
    await fetch(this.buildUrl('/api/job'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'pause', action: 'pause' }),
    })
    this.state.printerStatus = 'paused'
    this.emitEvent('print_paused')
  }

  async resumePrint(): Promise<void> {
    await fetch(this.buildUrl('/api/job'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'pause', action: 'resume' }),
    })
    this.state.printerStatus = 'printing'
    this.emitEvent('print_resumed')
  }

  async cancelPrint(): Promise<void> {
    await fetch(this.buildUrl('/api/job'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'cancel' }),
    })
    this.state.printerStatus = 'idle'
    this.emitEvent('print_cancelled')
  }

  async setNozzleTemp(temp: number): Promise<void> {
    await fetch(this.buildUrl('/api/printer/tool'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'target', targets: { tool0: temp } }),
    })
  }

  async setBedTemp(temp: number): Promise<void> {
    await fetch(this.buildUrl('/api/printer/bed'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'target', target: temp }),
    })
  }

  async homeAxes(axes?: ('x' | 'y' | 'z')[]): Promise<void> {
    await fetch(this.buildUrl('/api/printer/printhead'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'home', axes: axes || ['x', 'y', 'z'] }),
    })
  }

  async jogAxis(axis: 'x' | 'y' | 'z', distance: number): Promise<void> {
    const move: Record<string, number> = {}
    move[axis] = distance
    await fetch(this.buildUrl('/api/printer/printhead'), {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ command: 'jog', ...move }),
    })
  }

  async listFiles(): Promise<string[]> {
    const response = await fetch(this.buildUrl('/api/files?recursive=true'), {
      headers: this.getHeaders(),
    })
    const data = await response.json()
    return this.extractFileNames(data.files)
  }

  private extractFileNames(files: any[], prefix = ''): string[] {
    const names: string[] = []
    for (const file of files) {
      if (file.type === 'folder') {
        names.push(...this.extractFileNames(file.children || [], `${prefix}${file.name}/`))
      } else if (file.type === 'machinecode') {
        names.push(`${prefix}${file.name}`)
      }
    }
    return names
  }

  async uploadFile(file: File, filename?: string): Promise<void> {
    const formData = new FormData()
    formData.append('file', file, filename || file.name)

    await fetch(this.buildUrl('/api/files/local'), {
      method: 'POST',
      headers: { 'X-Api-Key': this.config.apiKey || '' },
      body: formData,
    })
  }

  async deleteFile(filename: string): Promise<void> {
    await fetch(this.buildUrl(`/api/files/local/${filename}`), {
      method: 'DELETE',
      headers: this.getHeaders(),
    })
  }

  private getHeaders(): HeadersInit {
    return {
      'X-Api-Key': this.config.apiKey || '',
      'Content-Type': 'application/json',
    }
  }
}
